import { ref } from 'vue'

export type DownReason = 'backend-down' | 'smtp-down' | 'db-down' | 'service-down'

export interface BackendDownInfo {
  source: 'heartbeat' | 'axios' | 'poll'
  reason: DownReason
  url: string
  status?: number
  statusText?: string
  message?: string
  /** Componenti Spring Actuator in stato DOWN, se disponibili */
  downComponents?: string[]
  timestamp: string
}

const isDown    = ref(false)
const wasDown   = ref(false)
const lastError = ref<BackendDownInfo | null>(null)

let pollTimer:      ReturnType<typeof setTimeout>  | null = null
let heartbeatTimer: ReturnType<typeof setInterval> | null = null
let verificaInCorso = false
/** Heartbeat falliti di fila: uno solo (timeout su un backend carico) non basta a dichiarare il backend giù. */
let heartbeatFalliti = 0

const PING_URL = '/api/v2/actuator/health'

/** Legge il body JSON dell'health endpoint e ricava i componenti DOWN */
async function parseHealthBody(res: Response): Promise<string[]> {
  try {
    const json = await res.clone().json()
    if (!json?.components) return []
    return Object.entries(json.components as Record<string, { status: string }>)
      .filter(([, v]) => v?.status === 'DOWN')
      .map(([k]) => k)
  } catch {
    return []
  }
}

/** Mappa una lista di componenti DOWN nel DownReason più specifico */
function toReason(downComponents: string[]): DownReason {
  if (downComponents.includes('mail')) return 'smtp-down'
  if (downComponents.includes('db') || downComponents.includes('diskSpace')) return 'db-down'
  if (downComponents.length > 0) return 'service-down'
  return 'backend-down'
}

interface PingResult {
  ok: boolean
  status?: number
  statusText?: string
  message?: string
  downComponents?: string[]
}

async function ping(): Promise<PingResult> {
  try {
    const res = await fetch(PING_URL, {
      method: 'GET',
      cache: 'no-store',
      signal: AbortSignal.timeout(8000),
    })

    // 2xx/3xx/4xx → server raggiungibile
    if (res.status < 500) return { ok: true, status: res.status }

    // 5xx → server up ma qualcosa è DOWN: leggi i componenti
    const downComponents = await parseHealthBody(res)
    return { ok: false, status: res.status, statusText: res.statusText, downComponents }
  } catch (e: unknown) {
    return { ok: false, message: e instanceof Error ? e.message : String(e) }
  }
}

function setError(source: BackendDownInfo['source'], result: PingResult, url = PING_URL) {
  const downComponents = result.downComponents ?? []
  lastError.value = {
    source,
    reason:         toReason(downComponents),
    url,
    status:         result.status,
    statusText:     result.statusText,
    message:        result.message,
    downComponents: downComponents.length > 0 ? downComponents : undefined,
    timestamp:      new Date().toISOString(),
  }
}

/**
 * Backend giù: controlla ogni 3 s e, appena risponde, toglie l'avviso.
 *
 * ⚠️ NON ricarica la pagina (lo faceva fino al 28/10/2026): il ricaricamento cancellava senza preavviso ciò che l'utente
 * stava scrivendo (una mail, un form) ogni volta che UNA richiesta falliva senza risposta, anche con il backend sano.
 * Il portale non ha stato da rinfrescare: la sessione è nel cookie e le richieste successive funzionano da sole.
 */
async function startPolling() {
  if (pollTimer) return
  const check = async () => {
    const result = await ping()
    if (result.ok) {
      isDown.value  = false
      wasDown.value = false
      pollTimer     = null
    } else {
      isDown.value  = true
      wasDown.value = true
      setError('poll', result)
      pollTimer = setTimeout(check, 3000)
    }
  }
  await check()
}

/**
 * Chiamata dall'interceptor axios quando una request non riceve risposta.
 */
export function reportNetworkError(ctx?: { url?: string; status?: number; message?: string }) {
  if (isDown.value || verificaInCorso) return
  // Una richiesta senza risposta può essere un timeout, una richiesta annullata, un battito di rete: non dice che il
  // backend è giù. Lo si dichiara giù solo se anche l'health non risponde.
  verificaInCorso = true
  void ping().then((result) => {
    if (result.ok) return
    isDown.value  = true
    wasDown.value = true
    lastError.value = {
      source:    'axios',
      reason:    'backend-down',
      url:       ctx?.url ?? '(sconosciuto)',
      status:    ctx?.status,
      message:   ctx?.message,
      timestamp: new Date().toISOString(),
    }
    void startPolling()
  }).finally(() => { verificaInCorso = false })
}

/**
 * Heartbeat ogni 15 s — rileva il backend down anche in idle.
 * Chiamare una sola volta al mount di App.vue.
 */
export function startHeartbeat() {
  if (heartbeatTimer) return
  heartbeatTimer = setInterval(async () => {
    if (isDown.value) return
    const result = await ping()
    if (result.ok) {
      heartbeatFalliti = 0
      return
    }
    if (++heartbeatFalliti < 2) return
    heartbeatFalliti = 0
    isDown.value  = true
    wasDown.value = true
    setError('heartbeat', result)
    void startPolling()
  }, 15_000)
}

export function useBackendStatus() {
  return { isDown, lastError }
}
